import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { ListItemIcon, MenuItem } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import reactDom from "react-dom";
import { useSelector } from "react-redux";
import { selectUserPermissionsValue } from "../../State/Slices/UserPermissionsSlice";
import { selectClientValue } from "../../State/Slices/SelectedClientSlice";
import {
  CheckAllRights,
  CheckAllRoles,
  CheckMultiRightsForClient,
  CheckRightsForClient,
  CheckRoleForClient,
  CheckCrmUserManagementRoleForClient,
} from "../../Helpers/PermissionsChecks";
import { RouteTypes } from "../../Enums/RouteTypes";

const useStyles = makeStyles({
  menuItem: {
    padding: "20px",
    justifyContent: "center",
  },
  itemIcon: {
    color: "white",
    justifyContent: "center",
  },
});

function checkRouteAccess(
  rolesObject,
  baseClientId,
  routeType,
  requiredPermissions
) {
  switch (routeType) {
    case RouteTypes.Unprotected:
      return true;
    case RouteTypes.RoleProtected:
      return CheckAllRoles(rolesObject, requiredPermissions);
    case RouteTypes.ClientRoleProtected:
      return CheckRoleForClient(rolesObject, baseClientId, requiredPermissions);
    case RouteTypes.RightProtected:
      return CheckAllRights(rolesObject, requiredPermissions);
    case RouteTypes.ClientRightProtected:
      return CheckRightsForClient(
        rolesObject,
        baseClientId,
        requiredPermissions
      );
    case RouteTypes.ClientRightsProtected:
      return CheckMultiRightsForClient(
        rolesObject,
        baseClientId,
        requiredPermissions
      );
    case RouteTypes.ClientCrmRightsProtected:
      return (
        CheckCrmUserManagementRoleForClient(rolesObject, baseClientId) ||
        CheckRightsForClient(rolesObject, baseClientId, requiredPermissions)
      );
    default:
      return false;
  }
}

export default function NavBarItem(props) {
  const classes = useStyles();
  const [hasRouteAccess, setHasRouteAccess] = useState(false);
  const [selected, setSelected] = useState(false);
  const rolesObject = useSelector(selectUserPermissionsValue);
  const selectedClient = useSelector(selectClientValue);

  const location = useLocation();

  useEffect(() => {
    setHasRouteAccess(
      checkRouteAccess(
        rolesObject,
        selectedClient.id?.toUpperCase(),
        props.routeType,
        props.requiredPermissions
      )
    );

    // If the main NavBar selection was changed due to a user click,
    // and the index of the new selection matches a NavBarItem's menuIndex,
    // set selected to true for the NavBarItem
    if (props.menuIndex === props.selectedNavBarIndex) {
      setSelected(true);
    }
    // If the main NavBar selection needs to change after a refresh/direct URL change,
    // and the NavBarItem's SubMenuRoutes contain the incoming route,
    // set selected to true for the NavBarItem
    else if (
      props.selectedNavBarIndex === 0 &&
      props.routes.includes(location.pathname)
    ) {
      setSelected(true);
      props.setSelectedNavBarIndex(props.menuIndex);
    } else {
      setSelected(false);
    }
  }, [
    props.navMenuSelection,
    props.menuCategory,
    rolesObject,
    selectedClient,
    props.routeType,
    props.requiredPermissions,
    location,
    props.routes,
    props.menuIndex,
    props.selectedNavBarIndex,
    props,
  ]);

  if (hasRouteAccess) {
    if (props.route === null) {
      return (
        <div>
          <Tooltip arrow placement="right" title={props.tooltipText}>
            <MenuItem
              onClick={props.clickEvent}
              className={classes.menuItem}
              selected={selected}
              sx={{
                minWidth: "95px",
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 0, 0, 0.08);",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08);",
                  },
                },
              }}
            >
              <ListItemIcon size="large" className={classes.itemIcon}>
                {props.icon}
              </ListItemIcon>
            </MenuItem>
          </Tooltip>
          {reactDom.createPortal(props.children, document.createElement("div"))}
        </div>
      );
    } else {
      return (
        <NavLink to={props.route}>
          <Tooltip arrow placement="right" title={props.tooltipText}>
            <MenuItem
              onClick={props.clickEvent}
              className={classes.menuItem}
              selected={props.menuIndex === props.selectedNavBarIndex}
              sx={{
                minWidth: "95px",
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 0, 0, 0.08);",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08);",
                  },
                },
              }}
            >
              <ListItemIcon className={classes.itemIcon}>
                {props.icon}
              </ListItemIcon>
            </MenuItem>
          </Tooltip>
        </NavLink>
      );
    }
  } else {
    return null;
  }
}
